import { Paths } from "components/common/navigation";
import { createError } from "core/exceptions";
import { loadAuth, saveAuth } from "core/localStorage";
import { assertArray, validObject } from "core/validation";
import log from "loglevel";
import { all, call, cancel, delay, put, race, retry, select, spawn, take, takeLatest } from "redux-saga/effects";
import { updateAxios } from "services/index";
import UserService, { ROLES } from "services/userService";
import envconfig from "../../../envconfig";
import { actions, isAuthenticatedSelector } from "./actions";
import { getExpirationDate, getTimeBeforeExpires, isValidStoreCredentials } from "components/login/logic/sagaHelpers";

const BG_TASKS = [];
export function* authUser(action) {
    const {
        payload: { username, password },
    } = action;
    try {
        log.debug("trying login", action);
        const response = yield call(UserService.auth, username, password);
        if (response.success) {
            yield call(_storeToken, { ...response, authenticationType: "jwt" });
            yield put(
                actions.AUTH_USER.success({
                    credentials: response,
                    authenticated: true,
                    authenticationType: "jwt",
                })
            );
            yield put(actions.INFO_USER.send());
            log.debug("login success for", username);
        } else {
            yield call(_storeToken, null);
            yield put(actions.AUTH_USER.failed(null, createError(response.error)));
        }
    } catch (e) {
        yield call(_storeToken, null);
        yield put(actions.AUTH_USER.failed(null, createError(e)));
    }
}

export function* authUserVerifySSO(action) {
    const {
        payload: { username },
    } = action;
    try {
        log.debug("trying login", action);
        const response = yield call(UserService.getUserInfo);
        if (response) {
            yield put(
                actions.AUTH_USER_VERIFY_SSO.success({
                    authenticated: true,
                    authenticationType: UserService.AUTH_TYPE_SSO,
                })
            );
            yield call(_storeToken, { authenticationType: "sso" });
            log.debug("login success(SSO) for", username);
        } else {
            throw new Error("User not found");
        }
    } catch (e) {
        yield call(_storeToken, null);
        yield put(actions.AUTH_USER_VERIFY_SSO.failed(null, createError(e)));
    }
}

export function* onLogout() {
    try {
        log.debug("loginout");
        yield call(_storeToken, null);
        yield call(UserService.logoutSSO);
        yield put(actions.LOGOUT.success());
        log.debug("loginout success");
    } catch (error) {
        log.error(error);
    }
}

export function* _storeToken(oauth) {

    let objToStore = null;
    if(validObject(oauth)) {
        objToStore= {
            ...oauth,
            expireDate: getExpirationDate(oauth),
        };
    }
    try {
        yield call(saveAuth, objToStore);
        updateAxios(oauth ? oauth.id_token : null);
        return true;
    } catch (error) {
        log.error(error);
    }
}

export function* _refreshToken() {
    try {
        const oauth = yield call(loadAuth);
        const newOauth = yield retry(
            envconfig.REFRESH_TOKEN_RETRY,
            envconfig.REFRESH_TOKEN_RETRY_INTERVAL_ML,
            UserService.refreshToken,
            oauth.id_refresh_token
        );
        yield call(_storeToken, { ...newOauth, authenticationType: "jwt" });
        yield put(actions.AUTH_USER.success({ credentials: newOauth, authenticated: true, authenticationType: "jwt" }));
        return newOauth;
    } catch (e) {
        yield call(_storeToken, null);
        yield put(actions.LOGOUT.success());
        log.error("refreshing token failed ", e, createError(e));
        return null;
    }
}

export function* authenticationTask() {
    try {
        while (true) {
            yield call(authentication);
        }
    } catch (error) {
        log.error(error);
    }
}

export function* startTask() {
    try {
        yield select(isAuthenticatedSelector);
    } catch (error) {
        log.error(error);
    }
}
export function* cancelTask() {
    try {
        if (assertArray(BG_TASKS)) {
            yield all(BG_TASKS.map((task) => cancel(task)));
        }
    } catch (error) {
        log.error(error);
    }
}

export function* authentication() {
    try {
        let userSignedOut;
        while (!userSignedOut) {
            let storedToken = yield call(loadAuth);
            log.debug("calling authentication", validObject(storedToken), isValidStoreCredentials(storedToken));
            log.debug(validObject(storedToken), isValidStoreCredentials(storedToken), storedToken);
            if (!validObject(storedToken) || !isValidStoreCredentials(storedToken)) {
                log.debug("wait for auth success");
                yield take(actions.AUTH_USER.successType);
                storedToken = yield call(loadAuth);
            }
            let tokenWillExpiredIn = getTimeBeforeExpires(storedToken.expire_in_seconds);
            log.debug("token will expired in", tokenWillExpiredIn);
            const { aboutToExpire } = yield race({
                signOutAction: take(actions.LOGOUT.successType),
                aboutToExpire: delay(tokenWillExpiredIn),
            });
            log.debug("about to expire", aboutToExpire);
            if (aboutToExpire) {
                log.debug("calling token-refresh");
                storedToken = yield call(_refreshToken);
                log.debug("token-refresh success", tokenWillExpiredIn);

                if (!storedToken) {
                    userSignedOut = true;
                    yield call(onLogout);
                }
            } else {
                userSignedOut = true;
                yield call(onLogout);
            }
        }
        return userSignedOut;
    } catch (error) {
        log.error(error);
    }
}

export function* loadCredentials(action) {
    try {
        let storedToken = yield call(loadAuth);
        if (isValidStoreCredentials(storedToken)) {
            yield call(_storeToken, storedToken);
            yield put(
                actions.AUTH_USER.success({
                    credentials: storedToken,
                    authenticated: true,
                    authenticationType: "jwt",
                })
            );
        } else if (storedToken && storedToken.authenticationType === "sso") {
            try {
                const userInfo = yield call(UserService.getUserInfo);
                if (userInfo) {
                    log.debug("Successfully validated user session");
                    yield put(actions.AUTH_USER_VERIFY_SSO.success({ credentials: storedToken, authenticated: true }));
                } else {
                    yield call(_storeToken, null);
                    yield put(actions.AUTH_USER_VERIFY_SSO.failed(null, createError(null)));
                }
            } catch (e) {
                yield call(_storeToken, null);
                yield put(actions.AUTH_USER_VERIFY_SSO.failed(null, createError(e)));
            }
        } else {
            yield call(_storeToken, null);
            if (action.payload.authenticated) {
                yield put(actions.LOGOUT.success({ authenticated: false }));
            }
        }
    } catch (error) {
        log.error(error);
    }
}

export function getUserPrivileges(user) {
    const isAdmin = !!isAdminRole(user);
    const isReadOnlyUser = isReadOnlyRole(user);
    let blockedPaths = [];
    if (!isAdmin) {
        blockedPaths = [Paths.ADMIN_USER, Paths.ADMIN_USER_EDIT];
    }
    if (isReadOnlyUser) {
        blockedPaths = [
            Paths.CONTACT,
            Paths.REPORT,
            Paths.ADV_REPORT,
            Paths.LOG_REPORT,
            Paths.STATISTICS,
            Paths.STATISTICS_TAGS,
            Paths.STATISTICS_USER,
            Paths.STATISTICS_CATEGORIES,
            Paths.ADMIN,
            Paths.ADMIN_API_IMPORT,
            Paths.ADMIN_TAGS,
            Paths.ADMIN_TEMPLATES,
            Paths.ADMIN_USER,
            Paths.ADMIN_USER_EDIT
        ];
    }
    return {
        isAdmin,
        isReadOnlyUser,
        canEditSecurity: isAdmin,
        canEditPassword: isAdmin,
        id: user.id,
        blockedPaths,
    };
}

export function isAdminRole(user) {
    return assertArray(user.roles) ? user.roles.find((r) => r.id === ROLES.ADMIN) : false;
}

export function isReadOnlyRole(user) {
    return user && assertArray(user.roles) ? user.roles.some(role => role.id === ROLES.READ_ONLY_USER) : false;
}

export default [

    spawn(authenticationTask),
    takeLatest(actions.AUTH_USER.sendType, authUser),
    takeLatest(actions.AUTH_USER_VERIFY_SSO.sendType, authUserVerifySSO),

    takeLatest(actions.ON_LOAD_CREDENTIALS.sendType, loadCredentials),
    takeLatest(actions.LOGOUT.sendType, onLogout),
    takeLatest(actions.AUTH_USER.successType, startTask),
    takeLatest(actions.LOGOUT.successType, cancelTask),
];
